import { withContext } from '@piggybank/core';
import { FieldContext } from '@piggybank/form';
import { CurrencyInput as PiggybankCurrencyInput } from '@piggybank/form/lib/es/components/CurrencyInput/CurrencyInput';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import T from 'prop-types';
import React, { useMemo } from 'react';
import { DF_KEYWORD_CUSTOM_FIELD } from '../../constants';
import { useCultureConfiguration } from '../../context';
import {
  extendKeyPath,
  formatCurrency,
  getCurrencySymbol,
  getSchemaAtCursor,
  parseCurrency,
} from '../../utils';
import { getCustomComponentDefinition } from '../../utils';

export const CurrencyInput = ({
  context,
  formValues,
  onChange,
  renderChildren,
  symbol,
  useCurrencyAsSymbol,
  validate,
  value,
  ...args
}) => {
  const culture = useCultureConfiguration();
  const schemaNode = getSchemaAtCursor(context.schemaPath, validate.schema);
  const amount = get(value, 'amount', get(schemaNode, 'default.amount', ''));

  const defaultCurrency = useMemo(
    () =>
      parseCurrency(
        get(schemaNode, 'default.currency', get(culture, 'currency'))
      ),
    [schemaNode, culture]
  );

  const currencyCode = get(value, 'currency', defaultCurrency.code);

  const inputSymbol = useMemo(
    () =>
      useCurrencyAsSymbol
        ? currencyCode
        : symbol != null
        ? symbol
        : getCurrencySymbol(currencyCode),
    [currencyCode, symbol, useCurrencyAsSymbol]
  );

  const renderCustomField = inputName => {
    const fieldSchemaNode = get(schemaNode, `properties.${inputName}`, {});

    const customFieldComponent = getCustomComponentDefinition(
      fieldSchemaNode,
      DF_KEYWORD_CUSTOM_FIELD
    );

    if (customFieldComponent) {
      return renderChildren({
        context: {
          ...context,
          keyPath: extendKeyPath(context.keyPath, { inputName }),
          schemaPath: [...context.schemaPath, 'properties', inputName],
        },
        formValues,
      });
    }

    return null;
  };

  const amountSchemaNode = get(schemaNode, 'properties.amount', {});
  const { maximum: maxAmount } = amountSchemaNode;

  return (
    <>
      <PiggybankCurrencyInput
        {...args}
        symbol={inputSymbol}
        {...(maxAmount
          ? {
              maxLength: String(maxAmount).length,
            }
          : {})}
        onChange={({ event, value }) => {
          onChange({
            value: {
              amount: value,
              currency: currencyCode,
            },
            event,
          });
        }}
        value={String(amount)}
      />

      {renderCustomField('amount')}
      {renderCustomField('currency')}
    </>
  );
};

CurrencyInput.formStructure = ['currency', 'amount'];

CurrencyInput.reviewFormatter = ({ culture, formData }) => {
  if (isEmpty(formData)) return '';

  return formatCurrency(
    formData.currency || get(culture, 'currency.code'),
    formData.amount,
    `${culture.lang}-${culture.countryCode}`
  );
};

CurrencyInput.propTypes = {
  context: T.shape({
    schemaPath: T.arrayOf(T.oneOfType([T.string, T.number])),
    keyPath: T.object,
  }),
  formValues: T.object,
  validate: T.oneOfType([T.object, T.func]),
  onChange: T.func,
  value: T.shape({
    currency: T.string,
    amount: T.oneOfType([T.string, T.number]),
  }),
  symbol: T.string,
  useCurrencyAsSymbol: T.bool,
  renderChildren: T.func,
};

export default withContext(FieldContext, props =>
  pick(props, [
    'name',
    'value',
    'onChange',
    'onBlur',
    'invalid',
    'required',
    'describers',
  ])
)(CurrencyInput);
